import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star, ShieldCheck, Headphones, Wallet, Plane, ArrowRight, TrendingUp, TrendingDown, BadgeCheck,
  Sparkles, Clock, Zap, CreditCard, Layers, Ticket, CalendarCheck, LifeBuoy, Search, ChevronRight,
} from "lucide-react";
import { SearchWidget } from "@/components/SearchWidget";
import { AirlineMarquee } from "@/components/AirlineMarquee";
import { PaymentLogos } from "@/components/PaymentLogos";
import { OfficeMap } from "@/components/OfficeMap";
import { DESTINATIONS } from "@/data/destinations";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";

const HERO_BG = [
  "https://images.unsplash.com/photo-1780734323790-6f18edf42997?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
  "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
  "https://images.pexels.com/photos/35072454/pexels-photo-35072454.jpeg?auto=compress&cs=tinysrgb&w=2400",
  "https://images.pexels.com/photos/16823629/pexels-photo-16823629.jpeg?auto=compress&cs=tinysrgb&w=2400",
];

const TRUST = [
  { icon: BadgeCheck, title: "UK Registered Company", text: "World Tour Hub Ltd · No. 17436422" },
  { icon: ShieldCheck, title: "Secure Payments", text: "Encrypted Stripe checkout" },
  { icon: Wallet, title: "Price Match Promise", text: "Lowest price, guaranteed" },
  { icon: Headphones, title: "24/7 Human Support", text: "Real travel experts, any time" },
];

const TRENDING = [
  { route: "London → Dubai", code: "LHR–DXB", price: 289, trend: -12, city: "Dubai" },
  { route: "London → New York", code: "LHR–JFK", price: 315, trend: -8, city: "New York" },
  { route: "London → Barcelona", code: "LHR–BCN", price: 79, trend: -5, city: "Barcelona" },
  { route: "London → Tokyo", code: "LHR–HND", price: 512, trend: 4, city: "Tokyo" },
  { route: "London → Bangkok", code: "LHR–BKK", price: 448, trend: -9, city: "Bangkok" },
  { route: "London → Paris", code: "LHR–CDG", price: 62, trend: -3, city: "Paris" },
];

const WHY = [
  { icon: Wallet, title: "Price Match Promise", text: "Find it cheaper elsewhere? We'll refund the difference — guaranteed lowest fares." },
  { icon: ShieldCheck, title: "Secure & Protected", text: "Bank-grade encrypted card payments and secure checkout on every booking." },
  { icon: Headphones, title: "24/7 Human Support", text: "Real travel experts, day or night, in 30+ languages whenever you need us." },
  { icon: BadgeCheck, title: "Free Cancellation", text: "Plans change. Most hotels and flights come with flexible, free cancellation." },
];

const BENEFITS = [
  { icon: Layers, title: "Combine & Save", text: "Special package prices only available when you book flight + hotel together." },
  { icon: Sparkles, title: "Made to measure", text: "Choose from thousands of flights, hotels and rooms to build your perfect trip." },
  { icon: CreditCard, title: "Book Now, Pay Later", text: "Reserve your dream trip with just a deposit — interest free, always." },
  { icon: ShieldCheck, title: "Everything you need", text: "Add insurance, extra luggage, car hire and transfers in a couple of taps." },
];

const useCountdown = (hoursAhead = 71) => {
  const target = React.useRef(Date.now() + hoursAhead * 3600 * 1000);
  const [t, setT] = React.useState({ d: 0, h: 0, m: 0, s: 0 });
  React.useEffect(() => {
    const tick = () => {
      let diff = Math.max(0, target.current - Date.now());
      const d = Math.floor(diff / 86400000); diff -= d * 86400000;
      const h = Math.floor(diff / 3600000); diff -= h * 3600000;
      const m = Math.floor(diff / 60000); diff -= m * 60000;
      const s = Math.floor(diff / 1000);
      setT({ d, h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return t;
};

const CountdownPill = () => {
  const { d, h, m, s } = useCountdown();
  const box = (v, l) => (
    <div className="flex flex-col items-center">
      <span className="font-heading font-black text-lg sm:text-xl text-white tabular-nums">{String(v).padStart(2, "0")}</span>
      <span className="text-[9px] uppercase tracking-wider text-white/70">{l}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-2 bg-slate-950 rounded-2xl px-4 py-2.5" data-testid="deal-countdown">
      <Clock className="w-4 h-4 text-brand mr-1" />
      {box(d, "days")}<span className="text-white/40 font-bold">:</span>{box(h, "hrs")}<span className="text-white/40 font-bold">:</span>{box(m, "min")}<span className="text-white/40 font-bold">:</span>{box(s, "sec")}
    </div>
  );
};

const HeroBackground = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % HERO_BG.length), 6000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="absolute inset-0">
      {HERO_BG.map((src, i) => (
        <div key={i} className={`absolute inset-0 transition-opacity duration-[1600ms] ease-out ${i === idx ? "opacity-100" : "opacity-0"}`}>
          <img src={src} alt="" className={`w-full h-full object-cover ${i === idx ? "hero-zoom" : ""}`} />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/55 to-slate-950/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-brand/40 via-transparent to-transparent" />
    </div>
  );
};

export default function Home() {
  const [packages, setPackages] = useState([]);
  const [reviews, setReviews] = useState({ average: 4.8, count: 0, results: [] });
  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data.results.slice(0, 4))).catch(() => {});
    api.get("/reviews?limit=6").then(({ data }) => setReviews(data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* ==================== CINEMATIC HERO ==================== */}
      <section className="relative min-h-[92vh] flex flex-col justify-center overflow-hidden" data-testid="hero">
        <HeroBackground />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 pt-14 pb-10">
          <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full glass-dark px-4 py-2 text-white/90 text-xs font-bold uppercase tracking-[0.18em]">
              <Sparkles className="w-4 h-4 text-brand" /> World Tour Hub · Award-winning travel
            </span>
            <h1 className="mt-6 font-heading font-black text-white text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02]">
              Your next journey,<br />
              <span className="text-shine">handpicked & unbeatable.</span>
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-white/80 max-w-xl font-medium">
              Compare flights, hotels, holiday packages and car hire from the world's best brands — all in one place, at guaranteed lowest prices.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-white/85 text-sm font-semibold">
              <span className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-emerald-400" /> ATOL-style protection</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Secure payments</span>
              <span className="flex items-center gap-2"><Wallet className="w-4 h-4 text-emerald-400" /> Price match promise</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="mt-9">
            <SearchWidget />
          </motion.div>
        </div>

        {/* Airline marquee anchored to hero base */}
        <div className="relative z-10 border-t border-white/10 bg-slate-950/60 backdrop-blur-md py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center text-[11px] font-bold uppercase tracking-[0.25em] text-white/50 mb-3">Fly with the world's finest airlines</div>
            <AirlineMarquee />
          </div>
        </div>
      </section>

      {/* ==================== TRUST BADGES ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {TRUST.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="rounded-2xl bg-white border border-slate-200 p-5 flex items-start gap-3 hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center shrink-0"><s.icon className="w-5 h-5 text-brand" /></div>
              <div>
                <div className="font-heading font-bold text-slate-950 leading-tight">{s.title}</div>
                <div className="text-sm text-slate-500 mt-0.5">{s.text}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ==================== TRENDING THIS WEEK ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-20" data-testid="trending-section">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Trending this week</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-slate-950 mt-2">Fares travellers are grabbing now</h2>
          </div>
          <Link to="/flights" className="hidden sm:flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-brand">Search flights <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2 -mx-1 px-1">
          {TRENDING.map((t, i) => {
            const down = t.trend < 0;
            return (
              <motion.div key={t.code} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="shrink-0 w-[230px]">
                <Link to={`/hotels?city=${t.city}`} data-testid={`trending-${i}`}
                  className="group block rounded-2xl border border-slate-200 bg-white p-5 hover:-translate-y-1 hover:shadow-xl hover:border-brand/30 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.code}</span>
                    <span className={`inline-flex items-center gap-1 text-xs font-bold ${down ? "text-emerald-600" : "text-rose-500"}`}>
                      {down ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}{Math.abs(t.trend)}%
                    </span>
                  </div>
                  <div className="font-heading font-bold text-lg text-slate-950 mt-2 flex items-center gap-1.5"><Plane className="w-4 h-4 text-brand" /> {t.route}</div>
                  <div className="mt-4 flex items-end justify-between">
                    <div><span className="text-xs text-slate-400">from</span><div className="font-heading font-black text-2xl text-slate-950">{money(t.price)}</div></div>
                    <span className="text-brand font-bold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">View <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 mt-3">Guide prices per person, one-way. Live fares connect at launch — real-time search once supplier keys are added.</p>
      </section>

      {/* ==================== POPULAR DESTINATIONS ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Trending now</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-slate-950 mt-2">Popular destinations</h2>
          </div>
          <Link to="/packages" className="hidden sm:flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-brand">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[200px]">
          {DESTINATIONS.map((d, i) => {
            const span = d.size === "large" ? "lg:col-span-2 lg:row-span-2" : d.size === "medium" ? "lg:col-span-2" : "";
            return (
              <motion.div key={d.name} initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className={`group relative rounded-2xl overflow-hidden cursor-pointer ${span}`}>
                <Link to={`/hotels?city=${d.name}`} data-testid={`destination-${d.name.toLowerCase().replace(" ", "-")}`}>
                  <img src={d.image} alt={d.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-5">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/80">{d.tag}</div>
                    <div className="font-heading font-extrabold text-white text-xl sm:text-2xl">{d.name}</div>
                    <div className="text-sm text-white/70">{d.country}</div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ==================== LAST MINUTE DEALS ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-24">
        <div className="rounded-3xl bg-gradient-to-br from-brand to-[#7a0046] p-6 sm:p-8 grain relative overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative">
            <div>
              <div className="inline-flex items-center gap-2 text-white font-heading font-black text-2xl sm:text-3xl tracking-tight"><Zap className="w-6 h-6 fill-white" /> LAST MINUTE DEALS</div>
              <p className="text-white/85 mt-1">Only here. And only for a short time — save up to 40% on flights + hotels.</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white/80 text-sm font-semibold">Ends in</span>
              <CountdownPill />
            </div>
          </div>
        </div>
        <div className="flex items-end justify-between mb-8 mt-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Flights + hotel · package prices</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-slate-950 mt-2">Handpicked holiday deals</h2>
          </div>
          <Link to="/packages" className="hidden sm:flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-brand">All packages <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {packages.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
              <Link to={`/packages/${p.id}`} data-testid={`deal-${i}`} className="group block rounded-2xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <div className="relative h-48 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-brand text-white text-xs font-bold">{p.tag}</span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-1 text-xs font-bold text-emerald-600 mb-1"><Star className="w-3.5 h-3.5 fill-emerald-500 text-emerald-500" />{p.rating} · {p.reviews_count} reviews</div>
                  <h3 className="font-heading font-bold text-lg text-slate-950 leading-snug">{p.title}</h3>
                  <div className="text-sm text-slate-500 mt-1">{p.nights} nights · flights included</div>
                  <div className="mt-4 flex items-end justify-between">
                    <div><span className="text-xs text-slate-400">from</span><div className="font-heading font-black text-2xl text-slate-950">{money(p.price)}</div></div>
                    <span className="text-brand font-bold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">View <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ==================== MANAGE MY BOOKING ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-24" data-testid="manage-booking-cta">
        <div className="relative rounded-3xl overflow-hidden bg-slate-950 grain">
          <img src="https://images.pexels.com/photos/16823629/pexels-photo-16823629.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/40" />
          <div className="relative grid lg:grid-cols-2 gap-8 p-8 sm:p-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-brand/20 text-brand px-4 py-1.5 text-xs font-bold uppercase tracking-wider"><Ticket className="w-4 h-4" /> Already booked with us?</span>
              <h2 className="mt-5 font-heading font-black text-3xl sm:text-4xl text-white tracking-tight">Manage My Booking</h2>
              <p className="mt-3 text-white/75 text-lg max-w-lg">
                Everything about your trip, in one place. View your itinerary, download e-tickets, change dates, add extras or request a refund — anytime, 24/7.
              </p>
              <div className="mt-6 grid sm:grid-cols-3 gap-3 max-w-lg">
                {[
                  { icon: CalendarCheck, t: "Change dates" },
                  { icon: Ticket, t: "Download tickets" },
                  { icon: LifeBuoy, t: "Get help fast" },
                ].map((x) => (
                  <div key={x.t} className="rounded-2xl glass-dark px-4 py-3 flex items-center gap-2 text-white text-sm font-semibold">
                    <x.icon className="w-4 h-4 text-brand" /> {x.t}
                  </div>
                ))}
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/manage-booking"><Button className="rounded-full bg-brand hover:bg-brand-hover text-white font-heading font-bold h-12 px-7 text-base" data-testid="manage-booking-btn"><Search className="w-4 h-4 mr-2" /> Find my booking</Button></Link>
                <Link to="/my-bookings"><Button variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 h-12 px-7 text-base font-heading font-bold">My trips</Button></Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="rounded-2xl glass-dark p-6">
                <div className="flex items-center justify-between">
                  <div className="text-white/60 text-xs font-bold uppercase tracking-wider">Booking reference</div>
                  <span className="text-emerald-400 text-xs font-bold flex items-center gap-1"><BadgeCheck className="w-4 h-4" /> Confirmed</span>
                </div>
                <div className="mt-2 font-heading font-black text-2xl text-white tracking-widest">WTH-8F42QK</div>
                <div className="mt-5 border-t border-white/10 pt-5 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-brand/20 flex items-center justify-center"><Plane className="w-6 h-6 text-brand" /></div>
                  <div className="flex-1">
                    <div className="text-white font-bold">London (LHR) → Malé (MLE)</div>
                    <div className="text-white/60 text-sm">Sat 12 Sep · 2 travellers · Emirates</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/40" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FLIGHT + HOTEL BENEFITS ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Flight + Hotel</span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-slate-950 mt-2">How booking together makes travel better</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {BENEFITS.map((b, i) => (
            <motion.div key={b.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="rounded-2xl bg-brand/5 border border-brand/10 p-6 text-center hover:bg-brand/10 transition-colors">
              <div className="w-14 h-14 rounded-2xl bg-brand flex items-center justify-center mx-auto mb-4 shadow-lg shadow-brand/30"><b.icon className="w-7 h-7 text-white" /></div>
              <h3 className="font-heading font-bold text-lg text-slate-950">{b.title}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ==================== WHY US ==================== */}
      <section className="bg-slate-950 mt-24 py-20 relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Why travellers choose us</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-white mt-2">Booking, perfected.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {WHY.map((w, i) => (
              <motion.div key={w.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-2xl bg-slate-900 border border-slate-800 p-6 hover:border-brand transition-colors">
                <div className="w-12 h-12 rounded-xl bg-brand/15 flex items-center justify-center mb-4"><w.icon className="w-6 h-6 text-brand" /></div>
                <h3 className="font-heading font-bold text-white text-lg">{w.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{w.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== HD REVIEWS ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-24">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Verified reviews · updated daily</span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-slate-950 mt-2">Real travellers, real reviews</h2>
          <div className="mt-4 inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-4 py-2">
            <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, j) => <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}</div>
            <span className="font-heading font-bold text-slate-950">{reviews.average}</span>
            <span className="text-slate-500 text-sm">· {reviews.count} verified reviews</span>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {reviews.results.map((t, i) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} data-testid={`review-${i}`}
              className="rounded-2xl bg-white border border-slate-200 p-7 shadow-sm hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex gap-0.5">{Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />)}</div>
                <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600"><BadgeCheck className="w-4 h-4" />{t.source}</span>
              </div>
              <p className="text-slate-700 leading-relaxed mt-3">"{t.text}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center font-heading font-bold">{t.name[0]}</div>
                <div className="flex-1"><div className="font-bold text-slate-950 text-sm">{t.name}</div><div className="text-xs text-slate-500">{t.location} · {new Date(t.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</div></div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ==================== OFFICE & MAP ==================== */}
      <OfficeMap />

      {/* ==================== PAYMENT / TRUST STRIP ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-24">
        <div className="rounded-3xl bg-slate-950 grain relative overflow-hidden p-8 sm:p-12">
          <div className="relative flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">100% secure checkout</span>
              <h2 className="font-heading font-black text-2xl sm:text-3xl text-white mt-2">Pay your way, with confidence</h2>
              <p className="text-white/70 mt-2 max-w-md">Every payment is encrypted and protected. Book with the cards and wallets you already trust.</p>
            </div>
            <div className="flex flex-col items-center gap-4">
              <PaymentLogos variant="light" className="justify-center" />
              <div className="flex items-center gap-2 text-white/60 text-xs font-semibold"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Powered by Stripe · PCI-DSS compliant</div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-16">
        <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand to-[#7a0046] p-10 sm:p-16 grain">
          <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-white/10" />
          <div className="absolute -right-8 bottom-0 opacity-20"><Plane className="w-64 h-64 text-white" /></div>
          <div className="relative max-w-xl">
            <TrendingUp className="w-10 h-10 text-white mb-4" />
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-white tracking-tight">Unlock members-only prices</h2>
            <p className="text-white/80 mt-3 text-lg">Create a free account to save up to 15% extra on secret deals, and manage every trip in one place.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/register"><Button className="rounded-full bg-white text-brand hover:bg-slate-100 font-heading font-bold h-12 px-7 text-base" data-testid="cta-register">Create free account</Button></Link>
              <Link to="/flights"><Button variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white/10 h-12 px-7 text-base font-heading font-bold">Start searching</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
